/* ============================================================================
   Resortes interrumpibles.

   Parámetros de diseñador (los de Apple), no de física:
     damping  — razón de amortiguamiento. 1.0 = sin rebote. <1 = rebota.
     response — qué tan rápido llega al objetivo, en segundos. No es duración:
                un resorte no tiene duración fija.

   Reglas que este módulo hace cumplir:
   · La animación SIEMPRE arranca del valor en pantalla (presentación), nunca del
     objetivo lógico. Re-apuntar conserva la velocidad → no hay "muro de ladrillo"
     al invertir un gesto.
   · Un solo rAF global para todos los resortes activos.
   · Con `prefers-reduced-motion: reduce` el resorte salta al objetivo.
   · GARANTÍA DE LLEGADA. Un resorte SIEMPRE termina en su objetivo, incluso si
     `requestAnimationFrame` se detiene: pestaña en segundo plano, ventana sin
     foco, o un panel embebido que no corre el bucle de animación. Cada animación
     arma un temporizador de respaldo con `setTimeout`, que sí corre en esos casos,
     y que fuerza el valor final. En una app de datos un número congelado a media
     interpolación no es un detalle estético: es una cifra equivocada en pantalla.
   ========================================================================== */

export interface SpringConfig {
  /** Razón de amortiguamiento. 1 = crítico (sin sobrepaso). ~0.8 = rebote leve. */
  damping?: number
  /** Segundos para llegar al objetivo. Más bajo = más ágil. */
  response?: number
}

/** Presets. Rebote SOLO cuando el gesto trajo inercia. */
export const SPRING = {
  /** Default de UI: crítico, sin sobrepaso. */
  ui: { damping: 1, response: 0.34 },
  /** Reposicionar algo (indicador de segmented control, etc.). */
  move: { damping: 1, response: 0.4 },
  /** Cajón / hoja arrastrable — el gesto trajo inercia. */
  sheet: { damping: 0.82, response: 0.3 },
  /** Suelta con inercia (flick). */
  momentum: { damping: 0.8, response: 0.4 },
  /** Cambio de datos: rápido y sin rebote, para que no distraiga del número. */
  data: { damping: 1, response: 0.28 },
} as const satisfies Record<string, Required<SpringConfig>>

const EPS_VALUE = 0.0015
const EPS_VELOCITY = 0.02
const MAX_FRAME = 1 / 30 // no integrar saltos grandes tras un tab en background
const SUBSTEP = 1 / 480 // integración estable incluso con response bajos

let reduceMotion = false
let reduceQuery: MediaQueryList | null = null
if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  reduceMotion = reduceQuery.matches
  reduceQuery.addEventListener('change', (e) => {
    reduceMotion = e.matches
  })
}

export function prefersReducedMotion(): boolean {
  return reduceMotion
}

type Listener = (value: number, velocity: number) => void

export class SpringValue {
  private _value: number
  private _velocity = 0
  private _target: number
  private _damping: number
  private _response: number
  private _listeners = new Set<Listener>()
  private _running = false
  private _watchdog: ReturnType<typeof setTimeout> | null = null

  constructor(initial: number, config: SpringConfig = SPRING.ui) {
    this._value = initial
    this._target = initial
    this._damping = config.damping ?? 1
    this._response = Math.max(config.response ?? 0.34, 0.01)
  }

  /** Valor de presentación: lo que está en pantalla ahora. */
  get value(): number {
    return this._value
  }

  get velocity(): number {
    return this._velocity
  }

  get target(): number {
    return this._target
  }

  get isAnimating(): boolean {
    return this._running
  }

  configure(config: SpringConfig): this {
    if (config.damping !== undefined) this._damping = config.damping
    if (config.response !== undefined) this._response = Math.max(config.response, 0.01)
    return this
  }

  /**
   * Re-apunta el resorte. Conserva el valor y la velocidad actuales, así que
   * puede llamarse en medio del vuelo sin discontinuidad.
   * `velocity` inyecta la velocidad de suelta del gesto (px/s, unidades del valor).
   */
  to(target: number, opts: SpringConfig & { velocity?: number } = {}): this {
    if (opts.damping !== undefined || opts.response !== undefined) this.configure(opts)
    this._target = target
    if (opts.velocity !== undefined) this._velocity = opts.velocity
    if (reduceMotion) {
      this._value = target
      this._velocity = 0
      this._emit()
      this._stop()
      return this
    }
    if (!this._settled()) this._start()
    return this
  }

  /** Salta al valor sin animar (p. ej. arrastre 1:1, o reset). */
  set(value: number, velocity = 0): this {
    this._value = value
    this._target = value
    this._velocity = velocity
    this._emit()
    this._stop()
    return this
  }

  /** Fija el valor de presentación sin tocar el objetivo (arrastre en curso). */
  track(value: number, velocity = 0): this {
    this._value = value
    this._velocity = velocity
    this._emit()
    this._stop()
    return this
  }

  onChange(fn: Listener): () => void {
    this._listeners.add(fn)
    return () => this._listeners.delete(fn)
  }

  /** @internal — lo llama el ticker global. */
  _step(dt: number): boolean {
    const omega = (2 * Math.PI) / this._response
    const k = omega * omega
    const c = 2 * this._damping * omega
    let remaining = Math.min(dt, MAX_FRAME)
    while (remaining > 0) {
      const h = Math.min(SUBSTEP, remaining)
      remaining -= h
      const accel = -k * (this._value - this._target) - c * this._velocity
      this._velocity += accel * h
      this._value += this._velocity * h
    }
    if (this._settled()) {
      this._value = this._target
      this._velocity = 0
      this._emit()
      return false
    }
    this._emit()
    return true
  }

  private _settled(): boolean {
    const scale = Math.max(1, Math.abs(this._target))
    return (
      Math.abs(this._value - this._target) < EPS_VALUE * scale &&
      Math.abs(this._velocity) < EPS_VELOCITY * scale
    )
  }

  private _emit(): void {
    for (const fn of this._listeners) fn(this._value, this._velocity)
  }

  private _start(): void {
    this._armWatchdog()
    if (this._running) return
    this._running = true
    register(this)
  }

  private _stop(): void {
    this._running = false
    this._clearWatchdog()
    unregister(this)
  }

  /**
   * Respaldo por temporizador. Un resorte crítico con `response` R se asienta en
   * poco más de R; damos 4R más un margen. Si el bucle de rAF no avanzó en ese
   * tiempo, saltamos al objetivo: preferimos perder la animación antes que dejar
   * un valor intermedio en pantalla.
   */
  private _armWatchdog(): void {
    this._clearWatchdog()
    if (typeof setTimeout !== 'function') return
    const budget = Math.ceil(this._response * 4000) + 400
    this._watchdog = setTimeout(() => {
      this._watchdog = null
      if (this._value === this._target) return
      this._value = this._target
      this._velocity = 0
      this._emit()
      this._running = false
      unregister(this)
    }, budget)
  }

  private _clearWatchdog(): void {
    if (this._watchdog !== null) {
      clearTimeout(this._watchdog)
      this._watchdog = null
    }
  }

  /** @internal — el ticker lo llama cuando el resorte ya llegó. */
  _markStopped(): void {
    this._running = false
    this._clearWatchdog()
  }

  destroy(): void {
    this._listeners.clear()
    this._stop()
  }
}

/* --- Ticker global ------------------------------------------------------- */

const active = new Set<SpringValue>()
let rafId: number | null = null
let lastTime = 0

function register(s: SpringValue): void {
  active.add(s)
  if (rafId === null && typeof requestAnimationFrame === 'function') {
    lastTime = performance.now()
    rafId = requestAnimationFrame(tick)
  }
}

function unregister(s: SpringValue): void {
  active.delete(s)
  if (active.size === 0 && rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
}

function tick(now: number): void {
  const dt = Math.max((now - lastTime) / 1000, 0)
  lastTime = now
  for (const s of Array.from(active)) {
    const keepGoing = s._step(dt)
    if (!keepGoing) {
      s._markStopped()
      active.delete(s)
    }
  }
  if (active.size > 0) {
    rafId = requestAnimationFrame(tick)
  } else {
    rafId = null
  }
}

/* --- Inercia y límites blandos ------------------------------------------ */

/**
 * Proyecta dónde se detendría un gesto con esta velocidad. Es la función exacta
 * del sample code de "Designing Fluid Interfaces" (decaimiento exponencial),
 * no la fórmula v²/2a de libro de texto.
 * @param velocity px/s al soltar
 * @param decelerationRate 0.998 ≈ scroll normal; 0.99 más ágil
 */
export function projectMomentum(velocity: number, decelerationRate = 0.998): number {
  return ((velocity / 1000) * decelerationRate) / (1 - decelerationRate)
}

/**
 * Resistencia progresiva al pasar de un límite. Nunca un tope duro: lo real
 * frena antes de detenerse.
 */
export function rubberband(overshoot: number, dimension: number, constant = 0.55): number {
  if (dimension <= 0) return 0
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot))
}

/** Aplica rubber-banding a un valor fuera de [min, max]. */
export function clampRubber(value: number, min: number, max: number, dimension: number): number {
  if (value < min) return min - rubberband(min - value, dimension)
  if (value > max) return max + rubberband(value - max, dimension)
  return value
}
